// Admin Settings → Etsy listing push → "Pushes" tab (plan P5; mockup approved
// by Volkan 2026-10-27). Counters for the period (each one is also a filter),
// search + result / store / period filters, one row per send with its six step
// dots, and a side panel with the step results, what was sent, Etsy's raw
// replies and the event log. Retry is offered only for a partly done send;
// an uncertain one is the seller's decision and is never retried here.

import { useDeferredValue, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { formatDateTime, formatTime } from '../../../lib/datetime'
import { toast } from '../../../lib/toast'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { Input } from '../../../components/ui/input'
import { Select } from '../../../components/ui/select'
import { useT } from '../../../i18n'
import { etsyPushAdminI18n, type EtsyPushAdminStrings } from '../../../i18n/admin/etsy-push-admin'
import { etsyPublishI18n } from '../../../i18n/seller/etsy-publish'
import { StepList } from '../../seller/etsy/EtsyPublishPanel'
import { STEP_ORDER, type PublishView } from '../../seller/etsy/etsy-publish.lib'
import {
  DEFAULT_FILTERS,
  badgeVariant,
  dotState,
  getJson,
  postJson,
  pushKey,
  pushesKey,
  pushesPath,
  type PushDetail,
  type PushFilters,
  type PushList,
  type PushRow,
  type ResultFilter,
} from './etsy-pushes.lib'

type StoreOption = { store_integration_id: string; shop_name: string }

const DOT = {
  ok: 'bg-[var(--color-success)]',
  warn: 'bg-[var(--color-warning)]',
  bad: 'bg-[var(--color-danger)]',
  none: 'bg-[var(--color-border)]',
} as const

export function EtsyPushesTab({ initialSelected = null }: { initialSelected?: string | null }) {
  const t = useT(etsyPushAdminI18n)
  // Opened from the go-live card's "Result": show that send, whatever its result.
  const [filters, setFilters] = useState<PushFilters>(
    initialSelected ? { ...DEFAULT_FILTERS, result: 'all', days: null } : DEFAULT_FILTERS,
  )
  const [selected, setSelected] = useState<string | null>(initialSelected)
  const q = useDeferredValue(filters.q)
  const query = { ...filters, q }
  const list = useQuery({
    queryKey: pushesKey(query),
    queryFn: () => getJson<PushList>(pushesPath(query)),
  })
  const stores = useQuery({
    queryKey: ['admin', 'etsy-listing', 'stores'],
    queryFn: async () => (await getJson<{ items: StoreOption[] }>('/admin/etsy-listing/stores')).items,
  })
  const set = (patch: Partial<PushFilters>) => setFilters((f) => ({ ...f, ...patch }))
  const counts = list.data?.counts

  const kpis: { id: ResultFilter; value: number | undefined; label: string; tone: string }[] = [
    { id: 'all', value: counts?.total, label: filters.days ? t.kpi.total(filters.days) : t.kpi.totalAll, tone: '' },
    { id: 'sent', value: counts?.sent, label: t.kpi.sent, tone: 'text-[var(--color-success)]' },
    { id: 'attention', value: counts?.attention, label: t.kpi.attention, tone: 'text-[var(--color-warning)]' },
    { id: 'failed', value: counts?.failed, label: t.kpi.failed, tone: 'text-[var(--color-danger)]' },
  ]

  return (
    <div className="space-y-4">
      <div role="group" aria-label={t.filters.result} className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
        {kpis.map((k) => (
          <button
            key={k.id}
            type="button"
            aria-pressed={filters.result === k.id}
            onClick={() => set({ result: k.id })}
            className={`rounded-[10px] border bg-[var(--color-surface)] px-3 py-2.5 text-left ${
              filters.result === k.id
                ? 'border-[var(--color-text)] shadow-[inset_0_0_0_1px_var(--color-text)]'
                : 'border-[var(--color-border)]'
            }`}
          >
            <div className={`text-[22px] font-bold tabular-nums ${k.tone}`}>{k.value ?? '—'}</div>
            <div className="text-[12.5px] text-[var(--color-text-muted)]">{k.label}</div>
          </button>
        ))}
      </div>

      <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="min-w-0 space-y-3 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="etsy-push-search"
              aria-label={t.filters.search}
              placeholder={t.filters.search}
              className="max-w-[300px]"
              value={filters.q}
              onChange={(e) => set({ q: e.target.value })}
            />
            <Select
              id="etsy-push-result"
              aria-label={t.filters.result}
              className="w-auto"
              value={filters.result}
              onChange={(e) => set({ result: e.target.value as ResultFilter })}
            >
              {(Object.keys(t.filters.results) as ResultFilter[]).map((id) => (
                <option key={id} value={id}>
                  {t.filters.results[id]}
                </option>
              ))}
            </Select>
            <Select
              id="etsy-push-store"
              aria-label={t.filters.store}
              className="w-auto"
              value={filters.store}
              onChange={(e) => set({ store: e.target.value })}
            >
              <option value="">{t.filters.allStores}</option>
              {(stores.data ?? []).map((s) => (
                <option key={s.store_integration_id} value={s.store_integration_id}>
                  {s.shop_name}
                </option>
              ))}
            </Select>
            <Select
              id="etsy-push-days"
              aria-label={t.filters.window}
              className="w-auto"
              value={filters.days ?? ''}
              onChange={(e) => set({ days: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="7">{t.filters.days(7)}</option>
              <option value="30">{t.filters.days(30)}</option>
              <option value="">{t.filters.allTime}</option>
            </Select>
          </div>
          {list.isError ? (
            <p role="status" className="text-sm text-[var(--color-danger)]">
              {t.detail.loadError}
            </p>
          ) : list.isLoading ? null : (list.data?.items ?? []).length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
          ) : (
            <DataTableShell variant="embedded">
              <PushTable t={t} rows={list.data?.items ?? []} selected={selected} onSelect={setSelected} />
            </DataTableShell>
          )}
          <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.rowHint}</p>
        </Card>
        {selected ? (
          <PushDetailPanel t={t} id={selected} />
        ) : (
          <Card className="space-y-1 p-4">
            <h3 className="text-[15px] font-semibold text-[var(--color-text)]">{t.detail.pick}</h3>
            <p className="text-sm text-[var(--color-text-muted)]">{t.detail.pickHint}</p>
          </Card>
        )}
      </div>
    </div>
  )
}

function PushTable({
  t,
  rows,
  selected,
  onSelect,
}: {
  t: EtsyPushAdminStrings
  rows: PushRow[]
  selected: string | null
  onSelect: (id: string) => void
}) {
  return (
    <table>
      <thead>
        <tr>
          <th>{t.table.when}</th>
          <th>{t.table.seller}</th>
          <th>{t.table.product}</th>
          <th>{t.table.steps}</th>
          <th>{t.table.result}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const done = STEP_ORDER.filter((s) => dotState(row.steps[s]) === 'ok').length
          return (
            <tr
              key={row.id}
              onClick={() => onSelect(row.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect(row.id)
                }
              }}
              tabIndex={0}
              aria-selected={selected === row.id}
              className={`cursor-pointer focus-visible:outline-2 focus-visible:outline-[var(--color-text)] ${
                selected === row.id ? '!bg-[var(--color-surface-muted)]' : ''
              }`}
            >
              <td className="whitespace-nowrap tabular-nums text-[var(--color-text-muted)]">
                {formatDateTime(row.updated_at ?? row.created_at)}
              </td>
              <td className="py-2 text-[var(--color-text)]">
                {row.org_name ?? '—'}
                <div className="text-xs text-[var(--color-text-muted)]">{row.shop_name ?? '—'}</div>
              </td>
              <td className="py-2 text-[var(--color-text)]">
                {row.listing_title ?? '—'}
                <div className="font-mono text-xs text-[var(--color-text-muted)]">
                  {row.external_listing_id
                    ? `#${row.external_listing_id}`
                    : row.status === 'uncertain'
                      ? t.table.unknownListing
                      : row.status === 'failed' || row.status === 'dead_letter'
                        ? t.table.noListing
                        : ''}
                </div>
              </td>
              <td>
                {row.is_v2 ? (
                  <span
                    role="img"
                    aria-label={t.table.stepsLabel(done, STEP_ORDER.length)}
                    className="inline-flex gap-[3px]"
                  >
                    {STEP_ORDER.map((s) => (
                      <i key={s} className={`inline-block h-[9px] w-[9px] rounded-full ${DOT[dotState(row.steps[s])]}`} />
                    ))}
                  </span>
                ) : (
                  <span className="text-xs text-[var(--color-text-muted)]">{t.table.legacy}</span>
                )}
              </td>
              <td>
                <Badge variant={badgeVariant(row.status)}>{t.status[row.status] ?? row.status}</Badge>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function PushDetailPanel({ t, id }: { t: EtsyPushAdminStrings; id: string }) {
  const tp = useT(etsyPublishI18n)
  const qc = useQueryClient()
  const detail = useQuery({
    queryKey: pushKey(id),
    queryFn: () => getJson<PushDetail>(`/admin/etsy-listing/pushes/${id}`),
    refetchInterval: (q) =>
      q.state.data && ['pending', 'in_progress'].includes(q.state.data.status) ? 2000 : false,
  })
  const retry = useMutation({
    mutationFn: () => postJson<PushDetail>(`/admin/etsy-listing/pushes/${id}/retry`),
    onSuccess: (data) => {
      toast.success(t.detail.retried)
      qc.setQueryData(pushKey(id), data)
      qc.invalidateQueries({ queryKey: ['admin', 'etsy-listing', 'pushes'] })
    },
    onError: (err: Error) => toast.error(t.detail.retryFailed, { description: err.message }),
  })

  if (detail.isError)
    return (
      <Card className="p-4">
        <p role="status" className="text-sm text-[var(--color-danger)]">
          {t.detail.loadError}
        </p>
      </Card>
    )
  const d = detail.data
  if (!d) return <Card className="p-4" aria-busy="true" />

  // Etsy's own words, per step and per failed photo (sellers never get these).
  const raws = Object.entries(d.step_results).flatMap(([step, r]) => [
    ...(r?.raw ? [`${step}: ${r.raw}`] : []),
    ...(r?.failed ?? [])
      .filter((f) => (f as { raw?: string | null }).raw)
      .map((f) => `${step} #${f.rank}: ${(f as { raw?: string | null }).raw}`),
  ])
  const rawText = raws.length ? raws.join('\n') : !d.is_v2 || d.status === 'failed' ? d.last_error : null
  const view = { status: d.status, steps: d.step_results } as unknown as PublishView
  const s = d.summary
  return (
    <Card className="min-w-0 space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant={badgeVariant(d.status)}>{t.status[d.status] ?? d.status}</Badge>
        <span className="text-xs tabular-nums text-[var(--color-text-muted)]">
          {formatDateTime(d.updated_at ?? d.created_at)}
          {d.duration_seconds !== null && (d.status === 'sent' || d.status === 'partial')
            ? ` · ${t.detail.seconds(d.duration_seconds)}`
            : ''}
        </span>
      </div>
      <div>
        <h3 className="text-base font-semibold text-[var(--color-text)]">{d.listing_title ?? '—'}</h3>
        <p className="m-0 text-xs text-[var(--color-text-muted)]">
          {[d.org_name, d.shop_name, d.sent_by ? t.detail.sentBy(d.sent_by) : null].filter(Boolean).join(' · ')}
        </p>
      </div>
      {d.edit_url ? (
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={d.edit_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-lg border border-[var(--color-border-strong)] px-2.5 py-1 text-[12.5px] font-semibold text-[var(--color-text)]"
          >
            {t.detail.openOnEtsy}
          </a>
          <span className="text-xs text-[var(--color-text-muted)]">
            {t.detail.listingNo(d.external_listing_id ?? '')}
          </span>
        </div>
      ) : null}
      {s ? (
        <div className="rounded-lg bg-[var(--color-surface-muted)] px-3 py-2 text-[12.5px]">
          <div className="font-semibold text-[var(--color-text)]">{t.detail.sentSummary}</div>
          <div className="text-[var(--color-text-muted)]">
            {t.detail.summary(s.rows, s.on_sale, s.photos)}
            {s.price_min && s.price_max ? ` · ${t.detail.priceRange(s.price_min, s.price_max)}` : ''}
          </div>
        </div>
      ) : null}
      {d.status === 'uncertain' ? (
        <div className="rounded-xl border border-[color-mix(in_srgb,var(--color-warning)_32%,transparent)] bg-[color-mix(in_srgb,var(--color-warning)_9%,var(--color-surface))] px-3 py-2.5">
          <div className="text-sm font-semibold text-[var(--color-text)]">{t.detail.uncertainTitle}</div>
          <p className="m-0 mt-0.5 text-xs text-[var(--color-text-muted)]">{t.detail.uncertainBody}</p>
        </div>
      ) : null}
      {d.is_v2 ? (
        <StepList t={tp} view={view} running={d.status === 'pending' || d.status === 'in_progress'} />
      ) : (
        <p className="text-xs text-[var(--color-text-muted)]">{t.detail.legacy}</p>
      )}
      {d.can_retry ? (
        <div className="space-y-1.5">
          <Button type="button" onClick={() => retry.mutate()} disabled={retry.isPending}>
            {retry.isPending ? t.detail.retrying : t.detail.retry}
          </Button>
          <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.detail.retryHint}</p>
        </div>
      ) : null}
      {d.status === 'uncertain' ? (
        <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.detail.uncertainReminder}</p>
      ) : null}
      {rawText ? (
        <details>
          <summary className="cursor-pointer text-xs text-[var(--color-text-muted)]">{t.detail.raw}</summary>
          <pre className="mt-1.5 whitespace-pre-wrap break-words rounded-lg bg-[var(--color-surface-muted)] p-2.5 font-mono text-xs text-[var(--color-text)]">
            {rawText}
          </pre>
        </details>
      ) : null}
      {d.events.length ? (
        <details>
          <summary className="cursor-pointer text-xs text-[var(--color-text-muted)]">{t.detail.log}</summary>
          <ul className="m-0 mt-1.5 grid list-none gap-1.5 p-0 text-[12.5px]">
            {d.events.map((e, i) => (
              <li key={`${e.at}-${i}`} className="grid grid-cols-[60px_minmax(0,1fr)] gap-2">
                <time className="tabular-nums text-[var(--color-text-muted)]">{formatTime(e.at)}</time>
                <span className="text-[var(--color-text)]">
                  {t.events[e.type] ?? e.type}
                  {e.actor_name ? ` (${e.actor_name})` : ''}
                  {typeof e.detail.step === 'string' ? ` · ${e.detail.step}` : ''}
                  {typeof e.detail.etsy_listing_id === 'string' ? ` · ${e.detail.etsy_listing_id}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  )
}
